import { v4 } from 'uuid';
import { z } from 'zod';

// src/geometry.ts
var GeometryType = /* @__PURE__ */ ((GeometryType2) => {
  GeometryType2["BOX"] = "box";
  GeometryType2["CYLINDER"] = "cylinder";
  GeometryType2["SPHERE"] = "sphere";
  GeometryType2["CONE"] = "cone";
  GeometryType2["EXTRUSION"] = "extrusion";
  GeometryType2["REVOLUTION"] = "revolution";
  GeometryType2["MESH"] = "mesh";
  return GeometryType2;
})(GeometryType || {});

// src/materials.ts
var MaterialType = /* @__PURE__ */ ((MaterialType2) => {
  MaterialType2["WOOD"] = "wood";
  MaterialType2["METAL"] = "metal";
  MaterialType2["PLASTIC"] = "plastic";
  MaterialType2["FABRIC"] = "fabric";
  MaterialType2["GLASS"] = "glass";
  MaterialType2["COMPOSITE"] = "composite";
  return MaterialType2;
})(MaterialType || {});
var WoodType = /* @__PURE__ */ ((WoodType2) => {
  WoodType2["OAK"] = "oak";
  WoodType2["WALNUT"] = "walnut";
  WoodType2["MAPLE"] = "maple";
  WoodType2["CHERRY"] = "cherry";
  WoodType2["PINE"] = "pine";
  WoodType2["BIRCH"] = "birch";
  WoodType2["PLYWOOD"] = "plywood";
  WoodType2["MDF"] = "mdf";
  WoodType2["PARTICLE_BOARD"] = "particle_board";
  return WoodType2;
})(WoodType || {});
var MetalType = /* @__PURE__ */ ((MetalType2) => {
  MetalType2["STEEL"] = "steel";
  MetalType2["STAINLESS_STEEL"] = "stainless_steel";
  MetalType2["ALUMINUM"] = "aluminum";
  MetalType2["BRASS"] = "brass";
  MetalType2["BRONZE"] = "bronze";
  MetalType2["IRON"] = "iron";
  return MetalType2;
})(MetalType || {});

// src/hardware.ts
var HardwareType = /* @__PURE__ */ ((HardwareType2) => {
  HardwareType2["HINGE_BUTT"] = "hinge_butt";
  HardwareType2["HINGE_CONCEALED"] = "hinge_concealed";
  HardwareType2["DRAWER_SLIDE"] = "drawer_slide";
  HardwareType2["KNOB"] = "knob";
  HardwareType2["PULL"] = "pull";
  HardwareType2["SCREW"] = "screw";
  return HardwareType2;
})(HardwareType || {});

// src/manufacturing.ts
var ManufacturingOperationType = /* @__PURE__ */ ((ManufacturingOperationType2) => {
  ManufacturingOperationType2["CUT"] = "cut";
  ManufacturingOperationType2["DRILL"] = "drill";
  ManufacturingOperationType2["ROUTE"] = "route";
  ManufacturingOperationType2["SAND"] = "sand";
  ManufacturingOperationType2["SHAPE"] = "shape";
  ManufacturingOperationType2["ASSEMBLE"] = "assemble";
  ManufacturingOperationType2["FINISH"] = "finish";
  ManufacturingOperationType2["EDGE_BAND"] = "edge_band";
  ManufacturingOperationType2["JOIN"] = "join";
  ManufacturingOperationType2["SURFACE"] = "surface";
  return ManufacturingOperationType2;
})(ManufacturingOperationType || {});
var AssemblyBuilder = class {
  constructor(name) {
    this.assembly = {
      id: v4(),
      name,
      version: "1.0.0",
      created_at: (/* @__PURE__ */ new Date()).toISOString(),
      updated_at: (/* @__PURE__ */ new Date()).toISOString(),
      components: [],
      joinery: []
    };
  }
  setDescription(description) {
    this.assembly.description = description;
    return this;
  }
  setAuthor(author) {
    this.assembly.author = author;
    return this;
  }
  addTags(tags) {
    this.assembly.tags = [...this.assembly.tags || [], ...tags];
    return this;
  }
  setCategory(category) {
    this.assembly.category = category;
    return this;
  }
  addComponent(component) {
    this.assembly.components.push(component);
    return this;
  }
  build() {
    if (!this.assembly.name) {
      throw new Error("Assembly must have a name");
    }
    this.assembly.bounds = this.calculateBounds();
    return this.assembly;
  }
  calculateBounds() {
    const components = this.assembly.components || [];
    if (components.length === 0) {
      return void 0;
    }
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const component of components) {
      const bounds = this.getComponentBounds(component);
      minX = Math.min(minX, bounds.min.x);
      minY = Math.min(minY, bounds.min.y);
      minZ = Math.min(minZ, bounds.min.z);
      maxX = Math.max(maxX, bounds.max.x);
      maxY = Math.max(maxY, bounds.max.y);
      maxZ = Math.max(maxZ, bounds.max.z);
    }
    return {
      min: { x: minX, y: minY, z: minZ },
      max: { x: maxX, y: maxY, z: maxZ },
      dimensions: {
        x: maxX - minX,
        y: maxY - minY,
        z: maxZ - minZ
      }
    };
  }
  getComponentBounds(component) {
    const pos = component.layout.transform.position;
    let halfSize;
    if (component.type === "panel" || component.type === "lumber") {
      const geom = component.geometry;
      halfSize = {
        x: geom.width / 2,
        y: geom.height / 2,
        z: geom.depth / 2
      };
    } else {
      halfSize = { x: 0.05, y: 0.05, z: 0.05 };
    }
    return {
      min: {
        x: pos.x - halfSize.x,
        y: pos.y - halfSize.y,
        z: pos.z - halfSize.z
      },
      max: {
        x: pos.x + halfSize.x,
        y: pos.y + halfSize.y,
        z: pos.z + halfSize.z
      }
    };
  }
};
var PanelBuilder = class {
  constructor() {
    this.panel = {
      id: v4(),
      type: "panel",
      layout: {
        transform: {
          position: { x: 0, y: 0, z: 0 },
          rotation: { x: 0, y: 0, z: 0, w: 1 },
          scale: { x: 1, y: 1, z: 1 }
        }
      }
    };
  }
  dimensions(width, height, thickness) {
    this.panel.geometry = {
      type: "box" /* BOX */,
      width,
      height,
      depth: thickness
    };
    return this;
  }
  material(type, subtype) {
    this.panel.material = {
      type,
      subtype,
      pbr_properties: {
        roughness: 0.8,
        metalness: 0
      }
    };
    return this;
  }
  position(x, y, z2) {
    this.panel.layout.transform.position = { x, y, z: z2 };
    return this;
  }
  rotation(x, y, z2, w) {
    this.panel.layout.transform.rotation = { x, y, z: z2, w };
    return this;
  }
  edgeBanding(edges, thickness, material) {
    this.panel.edge_banding = {
      edges,
      thickness,
      material
    };
    return this;
  }
  grainDirection(direction) {
    this.panel.grain_direction = direction;
    return this;
  }
  build() {
    if (!this.panel.geometry || !this.panel.material) {
      throw new Error("Panel must have geometry and material");
    }
    return this.panel;
  }
};
var Vector3DSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number()
});
var QuaternionSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
  w: z.number()
});
z.object({
  position: Vector3DSchema,
  rotation: QuaternionSchema,
  scale: Vector3DSchema
});
var AssemblySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  version: z.string(),
  author: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  components: z.array(z.any()),
  joinery: z.array(z.any()),
  bounds: z.object({
    min: Vector3DSchema,
    max: Vector3DSchema,
    dimensions: Vector3DSchema
  }).optional(),
  bom: z.any().optional(),
  manufacturing: z.any().optional(),
  tags: z.array(z.string()).optional(),
  category: z.string().optional(),
  metadata: z.record(z.any()).optional()
});
var FurnitureSerializer = class {
  static toJSON(assembly) {
    return JSON.stringify(assembly, null, 2);
  }
  static fromJSON(json) {
    const data = JSON.parse(json);
    const validated = AssemblySchema.parse(data);
    return validated;
  }
};

// src/utils.ts
function metersToInches(m) {
  return m * 39.3701;
}
function metersToFeet(m) {
  return m * 3.28084;
}
function cubicMetersToCubicFeet(m3) {
  return m3 * 35.3147;
}
function eulerToQuaternion(x, y, z2) {
  const c1 = Math.cos(x / 2);
  const c2 = Math.cos(y / 2);
  const c3 = Math.cos(z2 / 2);
  const s1 = Math.sin(x / 2);
  const s2 = Math.sin(y / 2);
  const s3 = Math.sin(z2 / 2);
  return {
    x: s1 * c2 * c3 + c1 * s2 * s3,
    y: c1 * s2 * c3 - s1 * c2 * s3,
    z: c1 * c2 * s3 + s1 * s2 * c3,
    w: c1 * c2 * c3 - s1 * s2 * s3
  };
}

export { AssemblyBuilder, AssemblySchema, FurnitureSerializer, GeometryType, HardwareType, ManufacturingOperationType, MaterialType, MetalType, PanelBuilder, WoodType, cubicMetersToCubicFeet, eulerToQuaternion, metersToFeet, metersToInches };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map